import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Typography } from '@mui/material';
import { DndChild, DndParentEntry } from '../types';
import { useSelectionContext } from '../contexts/SelectionContext';

export const DndChildCard = ({
  child,
  parent,
  columnId
}: {
  child: DndChild;
  parent: DndParentEntry;
  columnId: string;
}) => {
  const { isSelected, toggleSelect, isMultiDragActive, selectionSize, isDark, isReadOnly } =
    useSelectionContext();
  const isItemSelected = isSelected(columnId, 'child', child.id);
  const isDisabledDrag = selectionSize > 1 && !isItemSelected;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `child-${child.id}-column-${columnId}`,
    data: {
      type: 'child',
      item_id: parent.id,
      element: { ...parent, children: [child] },
      columnId
    },
    disabled: isDisabledDrag || isReadOnly
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const handleCtrlClick = (e: React.MouseEvent) => {
    if (isReadOnly) return;
    if (e.ctrlKey || e.metaKey) {
      e.stopPropagation();
      if (isSelected(columnId, 'parent', parent.id)) return;
      toggleSelect(columnId, 'child', child.id);
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClickCapture={handleCtrlClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        background: isItemSelected ? 'rgba(33,150,243,0.2)' : isDark ? '#252838' : '#fff',
        p: 1,
        borderRadius: 1,
        borderLeft: isItemSelected
          ? '4px solid #2196f3'
          : isDark
            ? '4px solid rgba(255,255,255,0.2)'
            : '4px solid #898989ff',
        opacity: isDragging || (isMultiDragActive && isItemSelected) ? 0 : 1,
        zIndex: isDragging ? 9999 : 'auto',
        transition: 'background 0.2s ease',
        cursor: isReadOnly ? 'default' : 'pointer'
      }}
    >
      {!isReadOnly && (
        <DragIndicatorIcon
          {...listeners}
          {...attributes}
          sx={{
            cursor: isDisabledDrag ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
            color: isItemSelected ? '#2196f3' : isDark ? 'rgba(255,255,255,0.4)' : '#898989ff',
            opacity: isDisabledDrag ? 0.3 : 1,
            '&:focus': { outline: 'none' }
          }}
        />
      )}
      <Box>
        <Typography
          variant="body2"
          fontWeight="bold"
          color={isDark ? 'rgba(255,255,255,0.87)' : 'grey.800'}
          fontSize={12}
        >
          {child.primaryLabel || `ID ${child.id}`}
        </Typography>
        {child.secondaryLabel && (
          <Typography variant="body2" color="textSecondary" fontSize={12}>
            {child.secondaryLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DndChildCard;
